/**
 * table.ts — shared pdfmake table builder with navy first column + zebra body.
 *
 * LAYOUT SEMANTICS (layout.fillColor):
 *   Header rows (rowIndex < headerRows): ALL columns → theme.primary (navy)
 *   Body rows col-0: ALWAYS theme.primary — zebra never overrides the number column
 *   Body rows col > 0 (odd body row): ZEBRA_COLOR alternating fill
 *   Body rows col > 0 (even body row): null (transparent, white page background)
 *
 * This produces a table where:
 *   - Header rows are entirely navy.
 *   - The first column is always navy on every body row.
 *   - Other columns alternate zebra / white by body-row index.
 *   - Repeating headers across page breaks via headerRows: 1.
 *   - Rows do not break across pages by default (dontBreakRows: true).
 */
import type { ContentTable, Content } from "pdfmake/interfaces.js";
import { autoContrast, subtleBorder, zebraFill } from "./color.js";

/** Content pages are white; borders/zebra derive from it so they stay visible & uniform. */
const PAGE_BG = "#ffffff";
const BORDER = subtleBorder(PAGE_BG);

export interface TableTheme {
  primary: string;
  accent?: string;
}

export interface BuildTableOptions {
  /** Override the default dontBreakRows: true. */
  dontBreakRows?: boolean;
  /** Text color for header cells. Defaults to "#ffffff". */
  headerTextColor?: string;
  /** Text alignment for header cells. Defaults to "left". */
  headerAlignment?: "left" | "center" | "right";
}

type CellContent = Content | string;

/**
 * Build a pdfmake ContentTable node with:
 * - Navy header row (all columns)
 * - Navy first column on ALL body rows (zebra never overrides col-0)
 * - Zebra alternating fill on col > 0 body rows
 * - Repeating header row across pages (headerRows: 1)
 * - No row breaks across pages by default
 *
 * @param head    - Column header labels.
 * @param widths  - Column widths (pt numbers or pdfmake strings like "*", "auto").
 * @param rows    - Body rows, each as an array of cell content values.
 * @param theme   - Brand theme providing the primary (navy) color.
 * @param opts    - Optional overrides for table behavior.
 */
export function buildTable(
  head: string[],
  widths: (string | number)[],
  rows: CellContent[][],
  theme: TableTheme,
  opts: BuildTableOptions = {},
): ContentTable {
  // Text painted on the primary-filled header row and col-0 must contrast
  // against primary (white vanishes when the admin picks a light primary).
  const onPrimary = autoContrast(theme.primary);
  const zebra = zebraFill(PAGE_BG, theme.primary);
  const { dontBreakRows = true, headerTextColor = onPrimary, headerAlignment = "left" } = opts;

  // Build header row with white text on primary background (fillColor handles the bg).
  // No cell-level `border` here: let the layout draw the full grid (outer box +
  // row separators) so this table matches §1 datos / §3 actividades.
  const headerRow: Content[] = head.map((h) => ({
    text: h,
    bold: true,
    color: headerTextColor,
    alignment: headerAlignment,
  }));

  const allRows: Content[][] = [headerRow, ...rows.map((row) =>
    row.map((cell, colIdx) =>
      typeof cell === "string"
        ? ({
            text: cell,
            // First column sits on primary fill → contrast against it; others dark ink on white.
            color: colIdx === 0 ? onPrimary : "#333333",
            fontSize: 9,
          } as Content)
        : cell,
    ),
  )];

  return {
    table: {
      headerRows: 1,
      dontBreakRows,
      keepWithHeaderRows: 1,
      widths,
      body: allRows,
    },
    layout: {
      fillColor: (rowIndex, node, columnIndex) => {
        const headerRows = (node.table.headerRows as number) ?? 1;
        // Header rows: primary for ALL columns.
        if (rowIndex < headerRows) return theme.primary;
        // Body rows col-0: ALWAYS primary (zebra must not override the number column).
        if (columnIndex === 0) return theme.primary;
        // Body rows col > 0: alternating zebra by body-row index.
        const bodyRow = rowIndex - headerRows;
        return bodyRow % 2 === 1 ? zebra : null;
      },
      hLineWidth: () => 0.5,
      hLineColor: () => BORDER,
      // Left (i=0) and right (i=numCols) outer verticals; no inner vertical separators (FIX-2).
      vLineWidth: (i, node) => {
        const cols = (node.table.widths as unknown[]).length;
        return i === 0 || i === cols ? 0.5 : 0;
      },
      vLineColor: () => BORDER,
      paddingLeft: () => 6,
      paddingRight: () => 6,
      paddingTop: () => 5,
      paddingBottom: () => 5,
    },
  } as ContentTable;
}
